import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'

// local imports - types
import calendarLoading from 'assets/icons/loading-calendar.png'
import { IFilteredGymTrainerList } from 'types/GymType'
import DateUtil from 'utils/DateUtil'
import Loader from 'components/ui/Loader'
import useGetGymTrainerReservationTable from '../../../hooks/use-get-gym-trainer-res-table'
import Scheduler from './Scheduler'
import ScheduleSidebar from './ScheduleSidebar'
import ReservationModalHeader from './ReservationModalHeader'
import WeekSlider from 'components/home/on-campus-services/health-service/appointment-scheduler/WeekSlider'
import { ActionArea, CancelButton, ConfirmButton } from '../../styled'

/* Types */
interface IModalFooterProps {
  disabled: boolean
  onCancel: () => void
  onSave: () => void
}

interface IAppointmentModalProps {
  filterDate: Date
  selectedTrainer: IFilteredGymTrainerList
  trainers: IFilteredGymTrainerList[] | []
  setSelectedTrainer: Dispatch<SetStateAction<IFilteredGymTrainerList | undefined>>
  open: boolean
  closeModal: (reset?: boolean) => void
  openModal: () => void
  showNotification: () => void
}

export default function ReservationModalWrapper({
  filterDate: dateFromList,
  trainers,
  selectedTrainer,
  setSelectedTrainer,
  open,
  closeModal,
  openModal,
  showNotification,
}: IAppointmentModalProps) {
  const { t } = useTranslation()

  /* Gym PT - Trainer reservationDate filter value for appointment date filtering etc. */
  const [filterDate, setFilterDate] = useState<Date>(dateFromList)
  useEffect(() => setFilterDate(dateFromList), [dateFromList])
  const lastFilteredDate = DateUtil.getWeekStartEndFromDate(dateFromList)

  /* GET Selected Gym Trainers Reservation Table List | Custom Hook  */
  const salonLocationId = selectedTrainer.gymSaloon.floor.locationId
  const salonId = selectedTrainer.id
  const {
    availableDates,
    tableWeek,
    setTableWeek,
    selectedAppointment,
    setSelectedAppointment,
    sidebarVisible,
    setSidebarVisible,
    loading,
    dailyAppointments,
  } = useGetGymTrainerReservationTable(salonLocationId, salonId, dateFromList)

  /* Open Reservation Sidebar */
  const openAppointmentSidebar = () => {
    setSidebarVisible(true)
    closeModal()
  }

  /* Close Sidebar and reset states */
  const closeSidebarAfterSave = () => {
    setSidebarVisible(false)
    setSelectedAppointment(undefined)
    showNotification()
    setFilterDate(dateFromList)
    setTableWeek(lastFilteredDate)
  }

  /* Close with cancel */
  const handleCloseOnCancel = () => {
    closeModal(true)
    setFilterDate(dateFromList)
    setSelectedAppointment(undefined)
    setTableWeek(lastFilteredDate)
  }

  return (
    <>
      <Dialog
        id="appointmentModal"
        blockScroll
        keepInViewport
        closeOnEscape={false}
        focusOnShow={false}
        closable={true}
        header={<span>{`${t('gym-res-modal.gym-reservation')} / ${t('gym-res-modal.trainer-res')}`}</span>}
        draggable={false}
        resizable={false}
        visible={open}
        style={{ width: '95vw', height: '95vh' }}
        breakpoints={{ '1536px': '95vw', '1280': '95vw' }}
        onHide={handleCloseOnCancel}
        footer={
          <FooterComponent
            onSave={openAppointmentSidebar}
            onCancel={handleCloseOnCancel}
            disabled={!selectedAppointment}
          />
        }>
        {/* Modal Header */}
        <ReservationModalHeader
          trainer={selectedTrainer}
          trainers={trainers}
          setSelectedTrainer={setSelectedTrainer}
          filterDate={filterDate}
          availableDateRange={availableDates}
          setFilterDate={setFilterDate}
          setTableWeek={setTableWeek}
        />

        {/* Week Change Slider */}
        <WeekSlider availableDateRange={availableDates} tableWeek={tableWeek} setTableWeek={setTableWeek} />

        {/* Content - Calendar View */}
        {loading ? (
          <Loader src={calendarLoading} />
        ) : (
          <Scheduler
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            tableWeek={tableWeek}
            dailyAppointments={dailyAppointments}
            selectedAppointment={selectedAppointment}
            setSelectedAppointment={setSelectedAppointment}
          />
        )}
      </Dialog>

      {/* Schedule - Save Appointment Sidebar */}
      {!!selectedAppointment && (
        <ScheduleSidebar
          openModal={openModal}
          visible={sidebarVisible}
          setVisible={setSidebarVisible}
          closeAfterSave={closeSidebarAfterSave}
          trainerInfo={selectedTrainer}
          appointmentInfo={selectedAppointment}
        />
      )}
    </>
  )
}

function FooterComponent({ onSave, onCancel, disabled }: IModalFooterProps) {
  const { t } = useTranslation()
  return (
    <ActionArea>
      <CancelButton id="closeModal" className="p-button-outlined" label={t('buttons.cancel')} onClick={onCancel} />
      <ConfirmButton id="openSidebar" disabled={disabled} label={t('buttons.appointment')} onClick={onSave} />
    </ActionArea>
  )
}
